'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { OFFLINE_AVATAR_MAX_RECORDING_DURATION_SECONDS, OFFLINE_AVATAR_MIN_AUDIO_DURATION_SECONDS } from '../constants';

export function useStudioRecorder(onComplete: (blob: Blob, duration: number) => void) {
  const [supported, setSupported] = useState(false);
  const [recording, setRecording] = useState(false);
  const [starting, setStarting] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<'unsupported' | 'denied' | 'too-short' | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef<number | null>(null);
  const activeRef = useRef(true);
  const requestRef = useRef(0);
  const callbackRef = useRef(onComplete);
  callbackRef.current = onComplete;

  const release = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    startedAtRef.current = null;
    streamRef.current?.getTracks().forEach((track) => {
      track.stop();
    });
    streamRef.current = null;
  }, []);

  useEffect(() => {
    activeRef.current = true;
    setSupported(typeof navigator.mediaDevices?.getUserMedia === 'function' && typeof MediaRecorder !== 'undefined');
    return () => {
      activeRef.current = false;
      requestRef.current += 1;
      if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
      release();
    };
  }, [release]);

  const stop = useCallback(() => {
    requestRef.current += 1;
    setStarting(false);
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
      return;
    }
    release();
    setRecording(false);
  }, [release]);

  const start = useCallback(async () => {
    if (starting || recording) return;
    if (!supported) {
      setError('unsupported');
      return;
    }
    setError(null);
    setStarting(true);
    const requestId = ++requestRef.current;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!activeRef.current || requestRef.current !== requestId) {
        stream.getTracks().forEach((track) => {
          track.stop();
        });
        return;
      }
      streamRef.current = stream;
      const mimeType = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm'].find((type) =>
        MediaRecorder.isTypeSupported(type),
      );
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = recorder;
      const chunks: BlobPart[] = [];
      const startedAt = performance.now();
      startedAtRef.current = startedAt;
      recorder.ondataavailable = ({ data }) => {
        if (data.size > 0) chunks.push(data);
      };
      recorder.onerror = () => {
        if (activeRef.current) {
          setError('denied');
          setRecording(false);
        }
        release();
      };
      recorder.onstop = () => {
        const duration = Math.min(
          (performance.now() - startedAt) / 1000,
          OFFLINE_AVATAR_MAX_RECORDING_DURATION_SECONDS,
        );
        release();
        if (!activeRef.current) return;
        setRecording(false);
        setElapsed(duration);
        if (duration < OFFLINE_AVATAR_MIN_AUDIO_DURATION_SECONDS) {
          setError('too-short');
        } else if (chunks.length) {
          callbackRef.current(new Blob(chunks, { type: recorder.mimeType }), duration);
        }
      };
      recorder.start(250);
      setElapsed(0);
      setRecording(true);
      timerRef.current = setInterval(() => {
        const duration = (performance.now() - startedAt) / 1000;
        setElapsed(duration);
        if (duration >= OFFLINE_AVATAR_MAX_RECORDING_DURATION_SECONDS) stop();
      }, 250);
    } catch {
      if (requestRef.current === requestId) {
        release();
        if (activeRef.current) setError('denied');
      }
    } finally {
      if (activeRef.current && requestRef.current === requestId) setStarting(false);
    }
  }, [recording, starting, supported, release, stop]);

  useEffect(() => {
    const syncElapsed = () => {
      if (!recording || startedAtRef.current === null) return;
      const duration = Math.min(
        (performance.now() - startedAtRef.current) / 1000,
        OFFLINE_AVATAR_MAX_RECORDING_DURATION_SECONDS,
      );
      setElapsed(duration);
      if (duration >= OFFLINE_AVATAR_MAX_RECORDING_DURATION_SECONDS) stop();
    };
    document.addEventListener('visibilitychange', syncElapsed);
    window.addEventListener('focus', syncElapsed);
    return () => {
      document.removeEventListener('visibilitychange', syncElapsed);
      window.removeEventListener('focus', syncElapsed);
    };
  }, [recording, stop]);

  return { supported, recording, starting, elapsed, error, start, stop };
}
